import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeftRight, GalleryHorizontalEnd } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RemovableBadge } from '@/components/ui/removable-badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { COMPARISON_TYPES, MAX_SEQUENCE_IMAGES, type ComparisonType } from '@constants/documentDetail';
import type { ImageComparisonState } from '@hooks/useImageComparison';

interface ImageComparisonControlsProps {
  comparison: ImageComparisonState;
}

/**
 * No background tint by design: the pre-port panel asked for `info.lighter`,
 * which the theme never defined, so only its border has ever rendered.
 */
const ImageComparisonControls: React.FC<ImageComparisonControlsProps> = ({ comparison }) => {
  const { t } = useTranslation();
  const { selectedImages } = comparison;

  return (
    <div className="mt-4 rounded-lg border border-primary/30 p-4">
      <p className="revy-cjk mb-2 text-sm font-bold leading-[1.5] text-primary">
        {t('documents.imageComparison.title')}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        {selectedImages.length > 0 && (
          <RemovableBadge
            variant="default"
            onRemove={comparison.clearSelection}
            removeLabel={t('common.clear')}
            className="revy-cjk"
          >
            {t('documents.imagesSelected', { count: selectedImages.length })}
          </RemovableBadge>
        )}

        {selectedImages.length === 2 && (
          <>
            <div className="flex flex-col gap-1">
              <Label
                htmlFor="image-comparison-type"
                className="revy-cjk text-xs leading-[1.5] text-muted-foreground"
              >
                {t('documents.imageComparison.type')}
              </Label>
              <Select
                value={comparison.comparisonType}
                onValueChange={(value) => comparison.setComparisonType(value as ComparisonType)}
              >
                <SelectTrigger id="image-comparison-type" className="h-8 min-w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="revy-ui">
                  {COMPARISON_TYPES.map((type) => (
                    <SelectItem key={type} value={type} className="revy-cjk">
                      {t(`documents.imageComparison.types.${type}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button size="sm" onClick={() => comparison.startComparison('compare')}>
              <ArrowLeftRight strokeWidth={1.9} />
              <span className="revy-cjk">{t('documents.imageComparison.compareTwo')}</span>
            </Button>
          </>
        )}

        {selectedImages.length >= 2 && selectedImages.length <= MAX_SEQUENCE_IMAGES && (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => comparison.startComparison('sequence')}
          >
            <GalleryHorizontalEnd strokeWidth={1.9} />
            <span className="revy-cjk">
              {t('documents.imageComparison.sequenceAnalysis', { count: selectedImages.length })}
            </span>
          </Button>
        )}

        {selectedImages.length === 0 && (
          <p className="revy-cjk text-xs leading-[1.5] text-muted-foreground">
            {t('documents.imageComparison.selectImagesHint')}
          </p>
        )}
      </div>
    </div>
  );
};

export default ImageComparisonControls;
